import React, { useCallback, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useDispatch } from "react-redux";

import IconMC from "../../components/IconMC";
import PaymentStatusTag from "./PaymentStatusTag";
import PaymentPlatformIcon from "./PaymentPlatformIcon";
import { formatMoney } from "../../../util/numberUtils";
import { customerDisplayName } from "../../../util/utils";
import { getRegionalConfig } from "../../../config";

import { SIDEBAR_ACTIONS } from "../../../constants/actionsConstants";
import RightModalLayout from "../../layouts/Modal/RightModalLayout";

// Helper component for info rows with IconMC
const InfoRow = ({ icon, label, children }) => (
  <div className="tw-flex tw-items-center">
    <div className="tw-flex tw-items-center tw-gap-3 tw-min-w-[180px]">
      <IconMC name={icon} size="lg" className="tw-text-blue-500" />
      <span className="tw-text-gray-600">{label}</span>
    </div>
    <span className="tw-text-gray-400 tw-mr-3">:</span>
    <div className="tw-flex-1 tw-text-gray-900 tw-font-medium">
      {children}
    </div>
  </div>
);

const DigitalPaymentModal = ({
  digitalPayment
}) => {
  const { t } = useTranslation('DigitalPayments');
  const dispatch = useDispatch();
  const { currency_symbol } = getRegionalConfig();
  const {
    amount, currency, order, customer, status, created_at, service, payment_method
  } = digitalPayment;

  const customerFullName = useMemo(() => {
    if (!customer) return '-';

    return customerDisplayName(customer) || '-';
  }, [customer]);

  const title = useMemo(() => {
    if (service === 'manual') {
      return `${t('transaction')} ${t('transaction_manual')}`;
    }

    return `${t('transaction')} #${digitalPayment.transaction_id ?? ''}`;
  }, [service, digitalPayment.transaction_id, t]);

  const closeModal = useCallback(() => {
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  }, [dispatch]);

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={title}
        closeAction={closeModal}
        className="tw-p-2"
      >
        <div className="tw-flex tw-flex-col tw-flex-1 tw-overflow-y-auto tw-gap-4">
          {/* Info Section */}
          <div className="tw-flex tw-flex-col tw-gap-2">
            <InfoRow icon="money" label={t('modal.amount')}>
              {currency_symbol}
              {formatMoney(amount || order?.total || 0)}
            </InfoRow>

            <InfoRow icon="person-outline" label={t('header.customer')}>
              {customerFullName || '-'}
            </InfoRow>

            <InfoRow icon="calendar-outline" label={t('header.date')}>
              {moment(created_at).local().format('DD/MM/YY')}
            </InfoRow>

            <InfoRow icon="credit-card-outline" label={t('modal.payment_method')}>
              {payment_method || t('modal.bank_card')}
            </InfoRow>

            <InfoRow icon="wallet-money-outline" label={t('header.platform')}>
              <PaymentPlatformIcon
                service={service || 'manual'}
                isManualSuccess={service === 'manual' && status === 'success'}
              />
            </InfoRow>

            <InfoRow icon="info-outline" label={t('header.status')}>
              <PaymentStatusTag status={status} />
            </InfoRow>
          </div>

          {/* Divider */}
          <div className="tw-border-t tw-border-gray-200" />

          {/* Payment Details Section */}
          <div className="tw-flex tw-flex-col tw-gap-2">
            <h4 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-mb-0">
              {t('modal.payment_details')}
            </h4>

            <div className="tw-flex tw-flex-col tw-gap-1">
              <div className="tw-flex tw-justify-between tw-text-sm tw-py-1">
                <span className="tw-text-gray-600">{t('modal.subtotal')}</span>
                <span className="tw-text-gray-900">
                  {formatMoney(amount || order?.subtotal || 0)}
                  {' '}
                  {currency}
                </span>
              </div>

              <div className="tw-flex tw-justify-between tw-text-sm tw-py-1">
                <span className="tw-text-gray-600">{t('modal.taxes')}</span>
                <span className="tw-text-gray-900">
                  {formatMoney(order?.tax_amount || 0)}
                  {' '}
                  {currency}
                </span>
              </div>

              <div className="tw-flex tw-justify-between tw-text-sm tw-py-1">
                <span className="tw-text-gray-600">{t('modal.shipping')}</span>
                <span className="tw-text-gray-900">
                  {order?.shipping_value ? `${formatMoney(order.shipping_value)} ${currency}` : '-'}
                </span>
              </div>

              <div className="tw-flex tw-justify-between tw-text-sm tw-font-semibold tw-py-1">
                <span className="tw-text-gray-900">{t('modal.total')}</span>
                <span className="tw-text-gray-900">
                  {formatMoney(amount || order?.total || 0)}
                  {' '}
                  {currency}
                </span>
              </div>
            </div>
          </div>
        </div>
      </RightModalLayout>
    </div>
  );
};

export default DigitalPaymentModal;
